
import { cn } from '../../lib/utils';

export type LoadingSpinnerSize = 'extra-small' | 'small' | 'medium' | 'large';
export type LoadingSpinnerVariant = 'motif' | 'spin';

export interface LoadingSpinnerProps {
  size?: LoadingSpinnerSize;
  /**
   * 'motif' renders the Airclou node-and-edge conveyor; 'spin' renders the
   * plain border spinner. Defaults by size: medium/large → motif,
   * extra-small/small → spin (the motif smears below ~20px).
   */
  variant?: LoadingSpinnerVariant;
  text?: string;
  textPosition?: 'right' | 'bottom';
  className?: string;
  spinnerClassName?: string;
  textClassName?: string;
  centerVertically?: boolean;
}

const sizeConfig = {
  'extra-small': {
    spinner: 'h-3 w-3',
    motif: 'h-3 w-9',
    text: 'text-xs',
    gap: 'gap-1.5'
  },
  small: {
    spinner: 'h-4 w-4',
    motif: 'h-4 w-12',
    text: 'text-sm',
    gap: 'gap-2'
  },
  medium: {
    spinner: 'h-5 w-5',
    motif: 'h-5 w-[60px]',
    text: 'text-base',
    gap: 'gap-3'
  },
  large: {
    spinner: 'h-8 w-8',
    motif: 'h-8 w-24',
    text: 'text-base',
    gap: 'gap-4'
  }
} as const;

/*
 * Conveyor motif: a workflow pipeline advancing through its steps. An edge
 * draws to the pending node, the node completes, and the whole pipeline
 * slides one 16-unit cell left. The loop is seamless only because the frame
 * at 100% (slid one cell) is pixel-identical to the frame at 0% — any change
 * to node positions, opacities, or which edges are drawn must preserve that
 * invariant or the restart becomes a visible snap.
 */
const conveyorCss = `
@keyframes aiu-conv-slide {
  0%, 52%   { transform: translateX(0); }
  88%, 100% { transform: translateX(-16px); }
}
@keyframes aiu-conv-edge {
  0%, 6%    { stroke-dashoffset: 9; }
  36%, 100% { stroke-dashoffset: 0; }
}
@keyframes aiu-conv-node {
  0%, 34%   { opacity: 0.25; transform: scale(1); }
  46%       { opacity: 1;    transform: scale(1.22); }
  58%, 100% { opacity: 1;    transform: scale(1); }
}
.aiu-conv { animation: aiu-conv-slide 1.7s cubic-bezier(0.55, 0, 0.3, 1) infinite; }
.aiu-conv-e2 {
  stroke-dasharray: 9;
  stroke-dashoffset: 9;
  stroke-linecap: round;
  animation: aiu-conv-edge 1.7s ease-in-out infinite;
}
.aiu-conv-n3 {
  transform-origin: 40px 12px;
  animation: aiu-conv-node 1.7s ease-in-out infinite;
}
@media (prefers-reduced-motion: reduce) {
  .aiu-conv, .aiu-conv-e2, .aiu-conv-n3 { animation: none; }
  .aiu-conv-e2 { stroke-dashoffset: 0; }
}
`;

function ConveyorMotif({ className }: { className?: string }) {
  // The viewBox hugs the drawn content (nodes span y 8.3–15.7 incl. the scale
  // blip) at a 3:1 ratio matching the size classes — restoring the full
  // 0–24 y-range shrinks the dots to ~60% at the same rendered height.
  return (
    <svg
      viewBox="0 4 48 16"
      fill="none"
      aria-hidden="true"
      className={cn('text-primary', className)}
    >
      <style>{conveyorCss}</style>
      <g className="aiu-conv">
        <line x1="11.5" y1="12" x2="20.5" y2="12" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" opacity="0.8" />
        <line x1="27.5" y1="12" x2="36.5" y2="12" stroke="currentColor" strokeWidth="1.6" opacity="0.8" className="aiu-conv-e2" />
        <circle cx="8" cy="12" r="3" fill="currentColor" />
        <circle cx="24" cy="12" r="3" fill="currentColor" />
        <circle cx="40" cy="12" r="3" fill="currentColor" opacity="0.25" className="aiu-conv-n3" />
        <circle cx="56" cy="12" r="3" fill="currentColor" opacity="0.25" />
      </g>
    </svg>
  );
}

export function LoadingSpinner({
  size = 'medium',
  variant,
  text,
  textPosition = 'right',
  className,
  spinnerClassName,
  textClassName,
  centerVertically = false
}: LoadingSpinnerProps) {
  const config = sizeConfig[size];
  const resolvedVariant: LoadingSpinnerVariant =
    variant ?? (size === 'medium' || size === 'large' ? 'motif' : 'spin');

  const spinnerElement =
    resolvedVariant === 'motif' ? (
      <ConveyorMotif className={cn(config.motif, spinnerClassName)} />
    ) : (
      <div
        className={cn(
          'animate-spin rounded-full border-b-2 border-gray-600 dark:border-gray-400',
          config.spinner,
          spinnerClassName
        )}
      />
    );

  const textElement = text && (
    <span
      className={cn(
        'text-muted-foreground',
        config.text,
        textClassName
      )}
    >
      {text}
    </span>
  );

  const containerClassName = cn(
    'flex items-center',
    config.gap,
    textPosition === 'bottom' ? 'flex-col' : 'flex-row',
    centerVertically && 'justify-center',
    className
  );

  return (
    <div className={containerClassName}>
      {spinnerElement}
      {textElement}
    </div>
  );
}

export default LoadingSpinner;
